import React, { useCallback, useMemo, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BellRing, Plus, Save } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Prompt, useParams } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/src/components/ui/button";

import NotificationTagModal from "./components/NotificationTagModal";
import { notificationColumns } from "./tableParams/notificationColumns";
import NotificationAPI from "../../api/NotificationAPI";
import CappedPage from "../../components/common/CappedPage";
import DataTable from "../../components/dataTable/DataTable";
import DataTableEmptyState from "../../components/dataTable/DataTableEmptyState";
import Breadcrumbs from "../../components/utils/Breadcrumbs";
import InfoPanel from "../../components/utils/InfoPanel";

import type { JSX } from "react";

import type {
  NotificationRouteParams,
  SaveOrderVariables,
} from "./NotificationsIndex.types";
import type { NotificationRow } from "../../api/NotificationAPI.types";

const INFO_PANEL_LINKS = {
  en: "https://support.okast.tv/en/support/solutions/articles/103000101783-create-and-manage-push-notifications-on-your-platform",
  fr: "https://support.okast.tv/fr/support/solutions/articles/103000101783-cr%C3%A9er-et-g%C3%A9rer-des-push-notifications-sur-sa-plateforme",
};

const EMPTY_ROW_LIST: NotificationRow[] = [];

function applyPendingOrder(
  tags: NotificationRow[],
  pendingOrder: string[] | null,
): NotificationRow[] {
  if (!pendingOrder) return tags;

  const tagsByUuid = new Map(tags.map((tag) => [tag.uuid, tag]));
  const orderedList = pendingOrder.flatMap((uuid) => {
    const tag = tagsByUuid.get(uuid);
    return tag ? [tag] : [];
  });
  const addedList = tags.filter((tag) => !pendingOrder.includes(tag.uuid));
  return [...orderedList, ...addedList];
}

function NotificationsIndex(): JSX.Element {
  const { t } = useTranslation("editor-notifications");
  const { projectSlug } = useParams<NotificationRouteParams>();
  const queryClient = useQueryClient();

  const getNotificationListQuery = NotificationAPI.generateQuery({
    fn: NotificationAPI.getNotificationList,
    params: [{ projectSlug }],
  });

  const { data, isLoading, isError, refetch } = useQuery(
    getNotificationListQuery,
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState<NotificationRow | null>(null);
  // Ordre glissé mais pas encore enregistré, par uuid : il survit aux relectures.
  const [pendingOrder, setPendingOrder] = useState<string[] | null>(null);

  const rowList = useMemo(
    () => applyPendingOrder(data ?? EMPTY_ROW_LIST, pendingOrder),
    [data, pendingOrder],
  );
  // Les rangs existants commencent à 0 ou à 1 : le plus grand + 1, pas le nombre de tags.
  const nextRank = data?.length
    ? Math.max(...data.map((tag) => tag.rank)) + 1
    : 0;

  const reloadList = (): void => {
    queryClient.invalidateQueries({
      queryKey: getNotificationListQuery.queryKey,
    });
  };

  const { mutate: deleteTag } = useMutation({
    mutationFn: async (uuid: string) =>
      NotificationAPI.deleteNotification({ projectSlug, uuid }),
    onSuccess: reloadList,
    onError: () => {
      toast.error(t("genericError", { ns: "error" }));
    },
  });

  const { mutate: saveOrder, isLoading: isSavingOrder } = useMutation({
    mutationFn: async ({ orderedRows }: SaveOrderVariables) => {
      const ranks = new Map((data ?? []).map((tag) => [tag.uuid, tag.rank]));
      const rankedList = orderedRows.map((tag, index) => ({
        ...tag,
        rank: index + 1,
      }));

      await Promise.all(
        rankedList
          .filter((tag) => ranks.get(tag.uuid) !== tag.rank)
          .map(async (tag) =>
            NotificationAPI.updateNotification({
              projectSlug,
              uuid: tag.uuid,
              notification: tag,
            }),
          ),
      );

      return rankedList;
    },
    onSuccess: (rankedList, { savedOrder }) => {
      queryClient.setQueryData(getNotificationListQuery.queryKey, rankedList);
      // Un déplacement fait pendant l'envoi reste en attente.
      setPendingOrder((current) => (current === savedOrder ? null : current));
    },
    onError: () => {
      toast.error(t("genericError", { ns: "error" }));
    },
  });

  const handleOpenCreation = (): void => {
    setCurrentItem(null);
    setIsModalOpen(true);
  };

  const handleOpenEdition = useCallback(
    (notification: NotificationRow): void => {
      setCurrentItem(notification);
      setIsModalOpen(true);
    },
    [],
  );

  const handleCloseModal = (): void => {
    setIsModalOpen(false);
    setCurrentItem(null);
  };

  const columnList = useMemo(
    () =>
      notificationColumns({
        t,
        onEdit: handleOpenEdition,
        onDelete: (notification) => deleteTag(notification.uuid),
      }),
    [t, handleOpenEdition, deleteTag],
  );

  const handleSaveOrder = (): void => {
    if (pendingOrder)
      saveOrder({ orderedRows: rowList, savedOrder: pendingOrder });
  };

  const handleReorder = (reorderedList: NotificationRow[]): void => {
    setPendingOrder(reorderedList.map((tag) => tag.uuid));
  };

  const handleSubmit = (): void => {
    handleCloseModal();
    reloadList();
  };

  const createButton = (
    <Button onClick={handleOpenCreation} size="lg" type="button">
      <Plus />
      {t("createCategorie")}
    </Button>
  );

  const headerActions = (
    <React.Fragment>
      {pendingOrder ? (
        <Button
          disabled={isSavingOrder}
          onClick={handleSaveOrder}
          size="lg"
          type="button"
        >
          <Save />
          {t("common:save")}
        </Button>
      ) : null}
      {rowList.length > 0 ? createButton : null}
    </React.Fragment>
  );

  const emptyState = (
    <DataTableEmptyState
      action={createButton}
      icon={BellRing}
      message={t("createCategorieText")}
      title={t("title")}
    />
  );

  return (
    <CappedPage contentClassName="pt-7.5 pb-15">
      <Prompt message={t("unsaved")} when={pendingOrder !== null} />
      <Breadcrumbs items={[{ name: t("editor-menu:notifications") }]} />
      <DataTable
        columns={columnList}
        data={rowList}
        description={<InfoPanel links={INFO_PANEL_LINKS} />}
        emptyState={emptyState}
        headerActions={headerActions}
        isError={isError}
        isLoading={isLoading}
        onReorder={handleReorder}
        onRetry={refetch}
        onRowClick={handleOpenEdition}
        title={`${t("title")} (${rowList.length})`}
      />
      <NotificationTagModal
        currentItem={currentItem}
        nextRank={nextRank}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        open={isModalOpen}
      />
    </CappedPage>
  );
}

export default NotificationsIndex;
